import { useRef, memo, useEffect, useState } from 'react'
import { useLoader } from '@react-three/fiber'
import * as THREE from 'three'
import { FBXLoader } from 'three/addons/loaders/FBXLoader.js'
import { buildImageUrl, normalizeAssetUrl } from '../../config'
import { useAppStore } from '../../stores/appStore'
import { applyPBR, isTextureReady } from './textureUtils'
import { isMajesticFabricMaterial, isMajesticTopper } from '../../utils/majesticTopper'

// Single shared loader instance - react-three-fiber caches by (loader, url) so this must be stable
const topperLoader = (() => {
  const manager = new THREE.LoadingManager()
  const originalResolveURL = manager.resolveURL.bind(manager)
  manager.resolveURL = (url: string) => {
    // Normalize all URLs first (HTTP -> HTTPS)
    const normalizedUrl = normalizeAssetUrl(url)
    
    if (/\.(jpg|jpeg|png|gif|bmp|tga)$/i.test(normalizedUrl)) {
      const filename = normalizedUrl.split(/[/\\]/).pop() || normalizedUrl
      return buildImageUrl(`/media/toppers/${filename}`)
    }
    // Normalize the resolved URL as well
    const resolved = originalResolveURL(normalizedUrl)
    return normalizeAssetUrl(resolved)
  }
  return new FBXLoader(manager)
})()

type Offsets = { x?: number; y?: number; z?: number }

function topperCloneTexturesAreReady(clone: THREE.Group | null): boolean {
  if (!clone) return false
  let hasMap = false
  let allReady = true
  clone.traverse((child: any) => {
    if (!child?.isMesh || !child.material) return
    const mats = Array.isArray(child.material) ? child.material : [child.material]
    mats.forEach((mat: any) => {
      const maps = [mat?.map, mat?.normalMap, mat?.roughnessMap, mat?.metalnessMap, mat?.aoMap]
      maps.forEach((tex) => {
        if (!tex) return
        hasMap = true
        if (!isTextureReady(tex)) allReady = false
      })
    })
  })
  return hasMap ? allReady : true
}

const MAJESTIC_APPLY_MAX_RETRIES = 120

function majesticSlotTexturesAreReady(clone: THREE.Group | null): boolean {
  if (!clone) return false
  let foundSlot = false
  let allReady = true
  clone.traverse((child: any) => {
    if (!child?.isMesh || !child.material) return
    const mats = Array.isArray(child.material) ? child.material : [child.material]
    mats.forEach((mat: any) => {
      const matName = mat?.name || ''
      if (!isMajesticFabricMaterial(matName)) return
      foundSlot = true
      if (!mat.map || !isTextureReady(mat.map)) allReady = false
    })
  })
  return foundSlot && allReady
}

function TopperCmp({ topperUrl, textureOptions, positionOffset = [0, 0, 0], topperSlug, topperSlugEn, offsets = {} as Offsets, renderReadyKey, onRenderReadyChange }: { 
  topperUrl: string
  textureOptions?: Array<{ slot_name: string; slot_label: string; textures: any[] }>
  positionOffset?: [number, number, number]
  topperSlug?: string
  topperSlugEn?: string
  offsets?: Offsets
  renderReadyKey?: string
  onRenderReadyChange?: (key: string, ready: boolean) => void
}) {
  const topper = useLoader(topperLoader, topperUrl)
  const BASE_SCALE = 0.05
  
  // Check if bed is adjustable and apply negative Y offset
  const bedVariation = useAppStore((s) => s.bedConfig.bed.variation)
  const bedTextile = useAppStore((s) => s.bedConfig.textile)
  const currentModel = useAppStore((s) => s.bedConfig.bed.currentModel)
  const isAdjustable = bedVariation?.bed_type_value === 'adjustable'
  const ADJUSTABLE_BED_TOPPER_Y_OFFSET = -1.12 // Very slightly raised from -1.18
  
  // Move topper slightly along the bed's length axis - only for adjustable beds
  // With BASE_SCALE = 0.05, 1 unit = 20cm
  const BACK_OFFSET_Z = 0.25 // Tuned visually for adjustable beds
  
  // Special Y offset for Majestic topper (lower position)
  const MAJESTIC_TOPPER_Y_OFFSET = -0.05 // Lower the Majestic topper slightly
  
  // Apply adjustable bed offsets (Y and Z) if needed
  const adjustedPositionOffset: [number, number, number] = isAdjustable
    ? [positionOffset[0], positionOffset[1] + ADJUSTABLE_BED_TOPPER_Y_OFFSET, positionOffset[2] + BACK_OFFSET_Z]
    : positionOffset
  const ox = Number(offsets.x ?? 0)
  const oy = Number(offsets.y ?? 0)
  const oz = Number(offsets.z ?? 0)
  
  // Track setup per topperUrl - cache clone, bounding box, Y position, AND bed max Y
  // Recalculate when topper OR bed model changes (seamless swap changes bed height)
  const setupRef = useRef<{
    topperUrl: string | null
    bedModelUrl: string | null
    clone: THREE.Group | null
    cloneBoxMinY: number | null
    topperY: number | null
    bedMaxY: number | null // Cache the bed's max Y when position is calculated
  }>({
    topperUrl: null,
    bedModelUrl: null,
    clone: null,
    cloneBoxMinY: null,
    topperY: null,
    bedMaxY: null,
  })
  
  const bedModelUrl = currentModel?.file_url || null

  const [bedTextileAppliedSignal, setBedTextileAppliedSignal] = useState(0)
  useEffect(() => {
    const onApplied = () => setBedTextileAppliedSignal((s) => s + 1)
    window.addEventListener('bed-textile-applied', onApplied)
    return () => window.removeEventListener('bed-textile-applied', onApplied)
  }, [])
  
  // Reset clone when topperUrl changes; reset Y when bed model changes
  if (setupRef.current.topperUrl !== topperUrl) {
    setupRef.current.topperUrl = topperUrl
    setupRef.current.bedModelUrl = null
    setupRef.current.clone = null
    setupRef.current.cloneBoxMinY = null
    setupRef.current.topperY = null
    setupRef.current.bedMaxY = null
  } else if (setupRef.current.bedModelUrl !== bedModelUrl) {
    // Same topper, new bed (e.g. seamless toggle) — keep clone, recompute Y
    setupRef.current.bedModelUrl = bedModelUrl
    setupRef.current.topperY = null
    setupRef.current.bedMaxY = null
  }
  
  // Clone and setup topper only once when topper loads
  if (!setupRef.current.clone && topper) {
    const c = topper.clone()
    c.scale.setScalar(BASE_SCALE)
    
    const tempBox = new THREE.Box3().setFromObject(topper)
    const tempCenter = tempBox.getCenter(new THREE.Vector3())
    const tempMin = tempBox.min
    
    c.position.set(-tempCenter.x * BASE_SCALE, -tempMin.y * BASE_SCALE, -tempCenter.z * BASE_SCALE)

    // Set shadow properties and create fresh materials to avoid FBX base color tints
    c.traverse((child: any) => {
      if (child.isMesh) {
        child.castShadow = true
        child.receiveShadow = true
        
        // Create fresh materials to avoid red/blue base colors from FBX
        const origMats = child.material
        const isArray = Array.isArray(origMats)
        const materials = isArray ? origMats : [origMats]
        
        const newMaterials = materials.map((origMat: any) => {
          // Create a fresh MeshStandardMaterial to avoid any FBX-imported tints/state
          const mat = new THREE.MeshStandardMaterial()
          
          // Preserve original material name so we can inspect slots later
          if (origMat?.name) {
            mat.name = origMat.name
          }
          
          // Set textile-like material defaults (similar to bed textiles)
          mat.color = new THREE.Color(0xdddddd) // Slightly darker than white for textile
          mat.emissive = new THREE.Color(0x000000)
          mat.vertexColors = false
          mat.roughness = 0.95 // High roughness for fabric-like appearance
          mat.metalness = 0.0 // No metalness for fabric
          ;(mat as any).envMapIntensity = 0.15 // Lower environment map intensity
          mat.aoMapIntensity = 1.0
          mat.normalScale = new THREE.Vector2(2.0, -2.0) // Invert Y for FBX conventions
          
          // Copy textures from original material if they exist.
          // Only set needsUpdate when texture has image data - avoids "no image data found" warning when a texture failed to load.
          if (origMat) {
            if (origMat.map) {
              mat.map = origMat.map
              if (mat.map) {
                mat.map.wrapS = THREE.RepeatWrapping
                mat.map.wrapT = THREE.RepeatWrapping
                if (isTextureReady(mat.map)) mat.map.needsUpdate = true
              }
            }
            if (origMat.normalMap) {
              mat.normalMap = origMat.normalMap
              if (mat.normalMap) {
                mat.normalMap.wrapS = THREE.RepeatWrapping
                mat.normalMap.wrapT = THREE.RepeatWrapping
                ;(mat.normalMap as any).colorSpace = (THREE as any).LinearSRGBColorSpace ?? (THREE as any).LinearEncoding
                if (isTextureReady(mat.normalMap)) mat.normalMap.needsUpdate = true
              }
            }
            if (origMat.roughnessMap) {
              mat.roughnessMap = origMat.roughnessMap
              if (mat.roughnessMap) {
                mat.roughnessMap.wrapS = THREE.RepeatWrapping
                mat.roughnessMap.wrapT = THREE.RepeatWrapping
                ;(mat.roughnessMap as any).colorSpace = (THREE as any).LinearSRGBColorSpace ?? (THREE as any).LinearEncoding
                if (isTextureReady(mat.roughnessMap)) mat.roughnessMap.needsUpdate = true
              }
            }
            if (origMat.metalnessMap) {
              mat.metalnessMap = origMat.metalnessMap
              if (mat.metalnessMap) {
                mat.metalnessMap.wrapS = THREE.RepeatWrapping
                mat.metalnessMap.wrapT = THREE.RepeatWrapping
                ;(mat.metalnessMap as any).colorSpace = (THREE as any).LinearSRGBColorSpace ?? (THREE as any).LinearEncoding
                if (isTextureReady(mat.metalnessMap)) mat.metalnessMap.needsUpdate = true
              }
            }
            if (origMat.aoMap) {
              mat.aoMap = origMat.aoMap
              if (mat.aoMap) {
                mat.aoMap.wrapS = THREE.RepeatWrapping
                mat.aoMap.wrapT = THREE.RepeatWrapping
                ;(mat.aoMap as any).colorSpace = (THREE as any).LinearSRGBColorSpace ?? (THREE as any).LinearEncoding
                if (isTextureReady(mat.aoMap)) mat.aoMap.needsUpdate = true
              }
            }
            if ((origMat as any).displacementMap) {
              (mat as any).displacementMap = (origMat as any).displacementMap
            }
            
            // Copy other important properties
            if (origMat.transparent !== undefined) mat.transparent = origMat.transparent
            if (origMat.opacity !== undefined) mat.opacity = origMat.opacity
          }
          
          mat.transparent = false
          mat.opacity = 1.0
          mat.depthTest = true
          mat.depthWrite = true
          mat.needsUpdate = true
          return mat
        })
        
        child.material = isArray ? newMaterials : newMaterials[0]
      }
    })

    // Extract unique material slot names for the topper model and expose globally
    const materialSlots = new Set<string>()
    c.traverse((child: any) => {
      if (child.isMesh && child.material) {
        const mats = Array.isArray(child.material) ? child.material : [child.material]
        mats.forEach((mat: any) => {
          if (mat?.name && mat.name !== 'unnamed') {
            materialSlots.add(mat.name)
          }
        })
      }
    })
    const sortedSlots = Array.from(materialSlots).sort()
    ;(window as any).__topper_model_material_slots__ = sortedSlots

    setupRef.current.clone = c
    
    // Calculate and cache the clone's bounding box min Y once (in local space)
    const cloneBox = new THREE.Box3().setFromObject(c)
    setupRef.current.cloneBoxMinY = cloneBox.min.y
    
    // Store local bounding box for visualization (before group positioning)
    ;(window as any).__topper_local_bbox__ = new THREE.Box3().copy(cloneBox)
  }

  // Track retry attempts for applying Majestic texture when bed repeat values aren't ready yet
  const [majesticRetryCount, setMajesticRetryCount] = useState(0)
  const [majesticTextureApplied, setMajesticTextureApplied] = useState(false)

  useEffect(() => {
    setMajesticTextureApplied(false)
    setMajesticRetryCount(0)
  }, [topperUrl, bedTextile?.currentTexture?.id])

  const majesticTopper = isMajesticTopper(topperSlug, topperSlugEn)

  // Keep Majestic topper's primary slot in sync with current bed textile
  useEffect(() => {
    if (!setupRef.current.clone) return
    if (!majesticTopper) return

    const c = setupRef.current.clone
    const bedBox = (window as any).__bed_bbox__
    const bedFinalRepeat = (window as any).__bed_textile_final_repeat__
    const bedBaseMap = (window as any).__bed_textile_base_map__ as THREE.Texture | null | undefined

    const prerequisitesReady =
      !!bedBaseMap &&
      isTextureReady(bedBaseMap) &&
      !!bedFinalRepeat &&
      !!bedBox &&
      !bedBox.isEmpty()

    if (!prerequisitesReady) {
      setMajesticTextureApplied(false)
      if (majesticRetryCount < MAJESTIC_APPLY_MAX_RETRIES) {
        const timeoutId = setTimeout(() => {
          setMajesticRetryCount((prev) => prev + 1)
        }, 100)
        return () => clearTimeout(timeoutId)
      }
      return
    }

    // Calculate size ratio: topper size / bed size
    const topperBox = new THREE.Box3().setFromObject(c)
    const topperSize = new THREE.Vector3().subVectors(topperBox.max, topperBox.min)
    const topperWidthCm = (topperSize.x / BASE_SCALE)
    const topperLengthCm = (topperSize.z / BASE_SCALE)
    
    const bedWidthCm = (bedBox.max.x - bedBox.min.x) / BASE_SCALE
    const bedLengthCm = (bedBox.max.z - bedBox.min.z) / BASE_SCALE
    
    // Scale bed's final repeat by the size ratio to maintain visual consistency
    const widthRatio = topperWidthCm / bedWidthCm
    const lengthRatio = topperLengthCm / bedLengthCm
    
    const repeatX = bedFinalRepeat.x * widthRatio
    const repeatY = bedFinalRepeat.y * lengthRatio

    let appliedCount = 0
    c.traverse((child: any) => {
      if (!child.isMesh || !child.material) return
      const mats = Array.isArray(child.material) ? child.material : [child.material]
      mats.forEach((mat: any) => {
        if (!isMajesticFabricMaterial(mat?.name)) return

        const repeat = { x: repeatX, y: repeatY }

        const clonedBase = bedBaseMap.clone()
        applyPBR({
          mat,
          origMat: child.material,
          type: 'textile',
          meshName: child.name,
          maps: { base: clonedBase, normal: null, roughness: null, metalness: null, ao: null, displacement: null },
          rotateTurns: 0,
          hasUV2: false,
          repeat,
          offset: { x: 0, y: 0 },
          rotation: 0,
        })
        mat.needsUpdate = true
        appliedCount += 1
      })
    })
    if (appliedCount === 0) {
      setMajesticTextureApplied(false)
      return
    }

    if (majesticSlotTexturesAreReady(c)) {
      setMajesticTextureApplied(true)
      return
    }

    setMajesticTextureApplied(false)
    if (majesticRetryCount < MAJESTIC_APPLY_MAX_RETRIES) {
      const timeoutId = setTimeout(() => {
        setMajesticRetryCount((prev) => prev + 1)
      }, 100)
      return () => clearTimeout(timeoutId)
    }
  }, [
    majesticTopper,
    topperSlug,
    topperSlugEn,
    bedTextile?.id,
    bedTextile?.currentTexture?.id,
    bedTextile?.textures,
    currentModel?.id,
    majesticRetryCount,
    bedTextileAppliedSignal,
  ])

  // Calculate Y position when clone is ready and bed bbox matches the current bed model.
  // Recomputes after seamless / model swaps (bedModelUrl change clears topperY above).
  if (setupRef.current.topperY === null && setupRef.current.cloneBoxMinY !== null) {
    const bedBox = (window as any).__bed_bbox__
    const bboxModelUrl = (window as any).__bed_bbox_model_url__ as string | null | undefined
    // Wait until Scene has published a bbox for this bed model (avoid using a stale one)
    if (
      bedBox &&
      !bedBox.isEmpty() &&
      isFinite(bedBox.max.y) &&
      (!bedModelUrl || bboxModelUrl === bedModelUrl)
    ) {
      setupRef.current.bedModelUrl = bedModelUrl
      setupRef.current.bedMaxY = bedBox.max.y
      setupRef.current.topperY = bedBox.max.y - setupRef.current.cloneBoxMinY + 0.002
    }
  }

  useEffect(() => {
    if (!renderReadyKey || !onRenderReadyChange) return
    const reportReady = () => {
      const clone = setupRef.current.clone
      const cloneReady = topperCloneTexturesAreReady(clone)
      const majesticReady = !majesticTopper || (majesticTextureApplied && majesticSlotTexturesAreReady(clone))
      const ready = setupRef.current.topperY !== null && cloneReady && majesticReady
      onRenderReadyChange(renderReadyKey, ready)
      return ready
    }
    // FBX image maps finish loading after the FBX callback. They mutate texture
    // objects without triggering React, so a one-off effect can stay false forever.
    if (reportReady()) return
    const timer = window.setInterval(() => {
      if (reportReady()) window.clearInterval(timer)
    }, 100)
    return () => window.clearInterval(timer)
  }, [
    renderReadyKey,
    topperUrl,
    onRenderReadyChange,
    topperSlug,
    topperSlugEn,
    majesticTopper,
    majesticTextureApplied,
    bedTextile?.currentTexture?.id,
    currentModel?.id,
    majesticRetryCount,
    bedTextileAppliedSignal,
  ])

  useEffect(() => {
    if (!renderReadyKey || !onRenderReadyChange) return
    return () => onRenderReadyChange(renderReadyKey, false)
  }, [renderReadyKey, onRenderReadyChange])

  const groupRef = useRef<THREE.Group>(null)

  // If we still don't have a valid Y position, don't render
  if (setupRef.current.topperY === null) {
    return null as any
  }

  // Use cached Y position - NEVER recalculate, even if bedBox changes
  const topperY = setupRef.current.topperY
  
  // Apply special Y offset for Majestic topper
  const majesticYOffset = majesticTopper ? MAJESTIC_TOPPER_Y_OFFSET : 0
  
  // Calculate topper base/top Y for other components
  if (setupRef.current.clone) {
    const cloneBox = new THREE.Box3().setFromObject(setupRef.current.clone)
    const groupY = topperY + (isAdjustable ? ADJUSTABLE_BED_TOPPER_Y_OFFSET : 0) + majesticYOffset
    const topperBaseY = groupY // visual base (where topper meets mattress)
    const topperTopY = groupY + cloneBox.max.y
    
    ;(window as any).__topper_base_y__ = topperBaseY
    ;(window as any).__topper_top_y__ = topperTopY
    ;(window as any).__topper_bbox__ = cloneBox
  }

  return (
    <group ref={groupRef} position={[
      adjustedPositionOffset[0] + ox,
      topperY + adjustedPositionOffset[1] + majesticYOffset + oy,
      adjustedPositionOffset[2] + oz
    ]}>
      <primitive object={setupRef.current.clone!} />
    </group>
  )
}

export default TopperCmp
